import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { addDays, daysBetween, relativeDay, shortDate } from '../../utils/taskDates';
import { firstName } from '../../utils/taskUsers';
import { itemUrl } from '../../utils/taskLinks';
import { whereLabel } from '../../utils/projectDashboard';
import ProgressCheck from './ProgressCheck';
import TaskIcon from './TaskIcon';
import '../../tasks.css';

const SHOW = 8;
/** Project tasks get a short section of their own under the rest. */
const PROJECT_SHOW = 3;
/** How far ahead a project task counts as pressing (due, or planned to start). */
const PROJECT_DAYS = 7;

/**
 * Dashboard card: the signed-in user's open tasks, most pressing first.
 * Project work would swamp it, so only the few most pressing project tasks
 * show, in their own section below everything else.
 */
const MyTasksCard: React.FC = () => {
  const { ready, error, items, lists, me, today, users, notifications, updateItem, openComposer } = useTasks();
  const navigate = useNavigate();

  const listTitle = useMemo(() => new Map(lists.map((l) => [l.id, l.title])), [lists]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const newIds = useMemo(() => {
    const ids = new Set(notifications.map((n) => n.item_id).filter(Boolean));
    // A notice about a week-list copy (a warehouse request) is news about the
    // original, which is the row this card shows.
    items.forEach((i) => {
      if (i.linked_to && ids.has(i.id)) ids.add(i.linked_to);
    });
    return ids;
  }, [notifications, items]);

  const { open, waiting, project, projectTotal } = useMemo(() => {
    // A week-list link and its original are one task; show it once.
    const ids = new Set(items.map((i) => i.id));
    const mine = items.filter(
      (i) => i.assignees.includes(me) && i.progress < 100 && !(i.linked_to && ids.has(i.linked_to))
    );
    const isWaiting = (i: TaskItem) => !!i.waiting_on && (!i.follow_up_date || i.follow_up_date > today);
    const rank = (i: TaskItem) => {
      if (i.due_date && i.due_date < today) return 0;
      if (i.due_date === today || (i.follow_up_date && i.follow_up_date <= today)) return 1;
      if (newIds.has(i.id)) return 2;
      if (i.due_date) return 3;
      return 4;
    };
    const byRank = (a: TaskItem, b: TaskItem) =>
      rank(a) - rank(b) || (a.due_date || '9').localeCompare(b.due_date || '9') || a.created_at.localeCompare(b.created_at);

    // A project task someone put on a week list is planned for this week, so
    // it's treated as ordinary work.
    const projectIds = new Set(lists.filter((l) => l.kind === 'project').map((l) => l.id));
    const isProject = (i: TaskItem) => projectIds.has(i.list_id) && !i.week_copy_list_id;
    const parents = new Set(items.map((i) => i.parent_id));
    const soon = addDays(today, PROJECT_DAYS);
    const pressing = (i: TaskItem) =>
      (!!i.due_date && i.due_date <= soon) ||
      (!!i.follow_up_date && i.follow_up_date <= today) ||
      newIds.has(i.id) ||
      (!!i.plan_start && i.plan_start <= soon);

    const active = mine.filter((i) => !isWaiting(i));
    // Leaves only: a branch is the sum of what's under it.
    const projectAll = active.filter((i) => isProject(i) && !parents.has(i.id));
    const projectItems = projectAll
      .filter(pressing)
      .sort((a, b) => byRank(a, b) || (a.plan_start || '9').localeCompare(b.plan_start || '9'));
    return {
      open: active.filter((i) => !isProject(i)).sort(byRank),
      waiting: mine.filter(isWaiting).length,
      project: projectItems,
      projectTotal: projectAll.length,
    };
  }, [items, lists, me, today, newIds]);

  const renderRow = (item: TaskItem, inProject = false) => {
    const overdue = item.due_date && item.due_date < today;
    const soon = item.due_date && !overdue && daysBetween(today, item.due_date) <= 1;
    return (
      <li key={item.id} className={`mt-row${newIds.has(item.id) ? ' is-new' : ''}`}>
        <ProgressCheck
          value={item.progress}
          onToggle={() => updateItem(item.id, { progress: 100 })}
          label={`Complete ${item.text}`}
        />
        <button type="button" className="mt-main" onClick={() => navigate(itemUrl(item, lists))}>
          <span className="mt-text">
            {item.text || 'Untitled'}
            {newIds.has(item.id) && <span className="ti-new-pill">New</span>}
          </span>
          <span className="mt-meta">
            {item.source === 'auto' && <TaskIcon name="auto" size={11} />}
            {item.source === 'recurring' && <TaskIcon name="recurring" size={11} />}
            {inProject ? whereLabel(item, byId, listTitle.get(item.list_id) || 'Untitled project') : listTitle.get(item.list_id)}
            {item.assigned_by && item.assigned_by !== me && ` · from ${firstName(users, item.assigned_by)}`}
          </span>
        </button>
        {item.due_date && (
          <span className={`tk-chip ${overdue ? 'tk-chip-danger' : soon ? 'tk-chip-warn' : ''}`}>
            <TaskIcon name="due" size={11} />
            {overdue ? `Overdue · ${shortDate(item.due_date)}` : relativeDay(item.due_date, today)}
          </span>
        )}
        {!item.due_date && item.plan_start && (
          <span className="tk-chip tk-chip-muted" title="Planned start">
            <TaskIcon name="gantt" size={11} />
            {item.plan_start <= today ? 'Under way' : relativeDay(item.plan_start, today)}
          </span>
        )}
        {item.follow_up_date && item.follow_up_date <= today && (
          <span className="tk-chip tk-chip-info"><TaskIcon name="waiting" size={11} />Follow up</span>
        )}
      </li>
    );
  };

  return (
    <div className="dash-card mt-card">
      <div className="dash-card-header">
        <h3>
          My Tasks {ready && <span className="mt-count">{open.length}</span>}
        </h3>
        <div className="mt-head-actions">
          <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={() => openComposer({})}>
            <TaskIcon name="plus" size={14} /> New task
          </button>
          <Link to="/lists" className="dash-link">Go to Lists →</Link>
        </div>
      </div>

      {!ready ? (
        <p className="dash-empty">{error || 'Loading…'}</p>
      ) : open.length === 0 ? (
        <p className="dash-empty">{project.length ? 'Nothing else assigned to you right now.' : 'Nothing assigned to you right now. 🎉'}</p>
      ) : (
        <ul className="mt-list">{open.slice(0, SHOW).map((i) => renderRow(i))}</ul>
      )}

      {ready && project.length > 0 && (
        <div className="mt-projects">
          <h4 className="mt-subhead">
            From projects
            <Link to="/projects" className="dash-link">
              {projectTotal > Math.min(project.length, PROJECT_SHOW) ? `All ${projectTotal} →` : 'Projects →'}
            </Link>
          </h4>
          <ul className="mt-list">{project.slice(0, PROJECT_SHOW).map((i) => renderRow(i, true))}</ul>
        </div>
      )}

      {ready && (open.length > SHOW || waiting > 0) && (
        <p className="dash-card-note mt-foot">
          {open.length > SHOW && <Link to="/lists" className="dash-link">+{open.length - SHOW} more</Link>}
          {open.length > SHOW && waiting > 0 && ' · '}
          {waiting > 0 && `${waiting} waiting on others`}
        </p>
      )}
    </div>
  );
};

export default MyTasksCard;
